import FieldTip from '../FieldTip.jsx';
import { SYNC_CADENCES, normalizeDemandDefaults } from '../../lib/settingsStore.js';
import {
  PAYMENT_PLAN_HINT,
  buildInstallmentPlanOptions,
} from '../../lib/installmentPlans.js';
import {
  SETTLEMENT_CHOICE_HINT,
  SETTLEMENT_CHOICES,
} from '../../lib/settlementChoice.js';

export default function AutomationsPanel({
  automations,
  demandDefaults,
  onDemandDefaultChange,
  onChange,
  onSubmit,
  pending,
}) {
  const defaults = normalizeDemandDefaults(demandDefaults);
  const planOptions = buildInstallmentPlanOptions();
  return (
    <form
      className="settings-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <h2>Automations</h2>
      <p className="settings-lead">
        Schedule accounting sync, reminders, credit-note alerts, and paid-demand archiving.
      </p>

      <div className="settings-subsection">
        <h3>Defaults for demands from a connected app</h3>
        <p className="settings-sublead">
          These answers are used when you send a demand from a connected app. You can still change
          them on the demand.
        </p>
        <div className="settings-grid">
          <label className="field">
            <span className="field-label-with-tip">
              Will you accept a payment plan?
              <FieldTip text={PAYMENT_PLAN_HINT} label="Payment plan" />
            </span>
            <select
              aria-label="Default payment plan"
              value={defaults.installmentPlan}
              onChange={(e) => onDemandDefaultChange({ installmentPlan: e.target.value })}
            >
              {planOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label-with-tip">
              Will you consider a settlement offer?
              <FieldTip text={SETTLEMENT_CHOICE_HINT} label="Settlement" />
            </span>
            <select
              aria-label="Default settlement offer"
              value={defaults.settlementChoice}
              onChange={(e) => onDemandDefaultChange({ settlementChoice: e.target.value })}
            >
              {SETTLEMENT_CHOICES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="settings-subsection">
        <h3>Accounting software sync</h3>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(automations.accountingSyncEnabled)}
            onChange={(e) => onChange({ accountingSyncEnabled: e.target.checked })}
          />
          <span>
            <strong>Automate accounting software sync</strong>
            <small>
              Import QuickBooks invoices on a schedule, using your last Sync invoices choice.
            </small>
          </span>
        </label>
        <label className="field">
          <span>Sync cadence</span>
          <select
            value={
              SYNC_CADENCES.some((c) => c.value === automations.accountingSyncCadence)
                ? automations.accountingSyncCadence
                : 'WEEKLY'
            }
            onChange={(e) => onChange({ accountingSyncCadence: e.target.value })}
            disabled={!automations.accountingSyncEnabled}
          >
            {SYNC_CADENCES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="settings-subsection">
        <h3>Reminders &amp; notes</h3>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(automations.automatedReminders)}
            onChange={(e) => onChange({ automatedReminders: e.target.checked })}
          />
          <span>
            <strong>Automated reminders</strong>
            <small>Follow up on unpaid demands and approaching payment-plan dates.</small>
          </span>
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(automations.creditNoteNotification)}
            onChange={(e) => onChange({ creditNoteNotification: e.target.checked })}
          />
          <span>
            <strong>Credit note notification</strong>
            <small>Alert when a credit note is issued in connected accounting software.</small>
          </span>
        </label>
      </div>

      <div className="settings-subsection">
        <h3>Archive</h3>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={Boolean(automations.autoArchivePaidDemands)}
            onChange={(e) => onChange({ autoArchivePaidDemands: e.target.checked })}
          />
          <span>
            <strong>Automatically archive paid demands</strong>
            <small>
              When you record a debtor payment (or a demand is marked Paid), move it to Archived.
              You can restore it anytime from Archived demands.
            </small>
          </span>
        </label>
      </div>

      <div className="drawer-actions">
        <button type="submit" className="btn-primary" disabled={pending}>
          {pending ? 'Saving…' : 'Save automations'}
        </button>
      </div>
    </form>
  );
}
